import { Component, effect, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MeterGroup } from 'primeng/metergroup';
import { TerminalLevel } from 'src/app/models/train-info.model';
import { IohTerminalEquipmentComponent } from 'src/app/shared/ioh-terminal-equipment/ioh-csx-terminal-equipment.component';
import { IohTerminalGateComponent } from 'src/app/shared/ioh-terminal-gate/ioh-csx-terminal-gate.component';
import { IohTerminalTrainComponent } from 'src/app/shared/ioh-terminal-train/ioh-csx-terminal-train.component';
import { IOHMapService } from 'src/app/services/ioh-map.service';
import { IOHAppService } from 'src/app/services/ioh-app.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'ioh-level-one-dashboard',
  templateUrl: './ioh-level-one-dashboard.component.html',
  styleUrls: ['./ioh-level-one-dashboard.component.scss'],
  imports: [
    CommonModule,
    MeterGroup,
    IohTerminalEquipmentComponent,
    IohTerminalGateComponent,
    IohTerminalTrainComponent
  ]
})
export class IohLevelOneDashboardComponent implements OnChanges {
  @Input() terminals: TerminalLevel[] = [];
  @Input() level = 0;
  @Input() iohShowFilter = false;
  @Input() iohEquipmentInfo: any[] = [];
  @Input() iohGateInfo: any = {};
  @Input() iohTrainInfo: { [key: string]: any } = {};
  @Input() iohNetworkStats: any = null;

  @Output() headerClick = new EventEmitter<TerminalLevel>();
  @Output() bodyClick = new EventEmitter<{ terminal: TerminalLevel; tab: string }>();

  @ViewChild('mapSlot') mapSlot?: ElementRef<HTMLElement>;

  constructor(
    private iohMapService: IOHMapService,
    private iohAppService: IOHAppService
  ) {
    effect(() => {
      const terminals = this.iohAppService.iohTerminalInfoRaw$();
      this.iohMapService.mapStyleReady();
      this.iohMapService.iohShowTerminalsOnMap(terminals, this.iohGateInfo);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.iohGateInfo && !changes.iohGateInfo.firstChange) {
      this.iohMapService.iohShowTerminalsOnMap(this.iohAppService.iohTerminalInfoRaw$(), this.iohGateInfo);
    }
  }

  ngAfterViewInit(): void {
    this.iohMapService.registerMapSlot('level1', this.mapSlot?.nativeElement ?? null);
  }

  ngOnDestroy(): void {
    this.iohMapService.registerMapSlot('level1', null);
  }

  env = environment;
  currentDate = new Date();
  dashboardTab = 'equipment';

  setDashboardTab(tab: string) {
    this.dashboardTab = tab;
    for (const terminal of this.terminals) {
      terminal.showEquipmentInfo = false;
      terminal.showGateInfo = false;
      terminal.showTrainInfo = false;
    }
  }

  onDashboardCardHeaderClick(terminal: TerminalLevel, event: Event) {
    event.stopPropagation();
    this.headerClick.emit(terminal);
  }

  onDashboardCardBodyClick(terminal: TerminalLevel, event: MouseEvent) {
    event.stopPropagation();
    this.closeOtherDashboardPopups(terminal);
    terminal.popupClickPoint = window.matchMedia('(max-width: 1179px)').matches
      ? null
      : { x: event.clientX, y: event.clientY };
    //keeps the popup within the card grid (e.g. off the map) for edge cards - if it still doesn't fully
    //fit vertically that's fine, the grid scrolls (see the scrollIntoView call below)
    const grid = (event.currentTarget as HTMLElement).closest('.ioh-terminal-card-grid') as HTMLElement;
    const gridRect = grid?.getBoundingClientRect();
    terminal.popupBounds = gridRect
      ? { left: gridRect.left, right: gridRect.right, top: gridRect.top, bottom: gridRect.bottom }
      : null;
    switch (this.dashboardTab) {
      case 'equipment':
        terminal.showEquipmentInfo = !terminal.showEquipmentInfo;
        break;
      case 'gate':
        terminal.showGateInfo = !terminal.showGateInfo;
        break;
      case 'trains':
        terminal.showTrainInfo = !terminal.showTrainInfo;
        break;
    }
    if (terminal.showEquipmentInfo || terminal.showGateInfo || terminal.showTrainInfo) {
      //scroll the actual popup (not just the card) into view - guarantees it stays fully inside the
      //grid's own scrollport regardless of whether the up/down flip guess above was exactly right
      const popupSuffix = this.dashboardTab === 'trains' ? 'train' : this.dashboardTab;
      const popupId = terminal.schematicId + '-dashboard-' + popupSuffix;
      setTimeout(() => document.getElementById(popupId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
    }
    this.bodyClick.emit({ terminal, tab: this.dashboardTab });
  }

  closeOtherDashboardPopups(currentTerminal: TerminalLevel) {
    for (const terminal of this.terminals) {
      if (terminal === currentTerminal) continue;
      terminal.showEquipmentInfo = false;
      terminal.showGateInfo = false;
      terminal.showTrainInfo = false;
    }
  }

  getTerminalCode(code) {
    return code == 'NOW' ? 'NWO' : code == 'PHI' ? 'GRE' : code;
  }

  //to get equipment info for terminal
  getEquipmentTableInfo(terminal: string) {
    let rawTableResponse = this.iohEquipmentInfo?.find(item =>
      terminal.toUpperCase().includes(item.terminalName)
    );
    if (rawTableResponse) {
      return this.processEquipmentTableData(rawTableResponse?.assetDetails);
    }
    return null;
  }

  //to process equipment info table data
  processEquipmentTableData(rawTableResponse: any) {
    let totalActiveCount = 0;
    let totalOfflineCount = 0;
    let totalIdleCount = 0;
    rawTableResponse.forEach(entry => {
      if (entry.vehicleType === 'CHE' || entry.vehicleType === 'WSC_CLIENT') {
        totalActiveCount += entry.activeCount;
        totalOfflineCount += entry.offlineCount;
        totalIdleCount += entry.idleCount;
      }
    });
    return {
      totalActiveCount,
      totalOfflineCount,
      totalIdleCount
    };
  }

  totalInboundTrains() { return this.iohTrainInfo?.totals?.totalInboundTrains || 0; }
  totalOutboundTrains() { return this.iohTrainInfo?.totals?.totalOutboundTrains || 0; }

  avgGateDwell() {
    const avgTurnTimeMinutes = this.iohGateInfo?.totals?.avgTurnTimeMinutes;
    return avgTurnTimeMinutes ? `${Math.round(avgTurnTimeMinutes)} min` : '0 min';
  }
  totalLifts() { return this.iohNetworkStats?.liftInfo?.totalLifts || 0; }
  totalLogs() { return this.iohNetworkStats?.logsInfo?.totalLogs || 0; }
  totalBookings() { return this.iohNetworkStats?.bookingsInfo?.totalBookings || 0; }

  terminalStatus(terminal: TerminalLevel) {
    const gateMinutes = this.iohGateInfo?.terminals?.[this.getTerminalCode(terminal.code)]?.avgTurnTimeMinutes;
    if (gateMinutes > this.env.appConfig.iohGateAvgTurnTimeMints) return 'Needs Attention';
    if ((this.getEquipmentTableInfo(terminal.name)?.totalOfflineCount || 0) > 0) return 'Below Average';
    return 'Above Average';
  }

  terminalStatusClass(terminal: TerminalLevel) { return this.terminalStatus(terminal).toLowerCase().replace(' ', '-'); }

  getEquipmentMetrics(terminal: TerminalLevel) {
    const equipment = this.getEquipmentTableInfo(terminal.name);
    return { active: equipment?.totalActiveCount || 0, idle: equipment?.totalIdleCount || 0, inactive: equipment?.totalOfflineCount || 0 };
  }

  getEquipmentMeterValues(terminal: TerminalLevel) {
    const metrics = this.getEquipmentMetrics(terminal);
    return [
      { label: 'Active', value: metrics.active, color: '#58c56c' },
      { label: 'Idle', value: metrics.idle, color: '#f5b322' },
      { label: 'Inactive', value: metrics.inactive, color: '#ef655e' }
    ];
  }

  getGateMetrics(terminal: TerminalLevel) {
    const gate = this.iohGateInfo?.terminals?.[this.getTerminalCode(terminal.code)] || {};
    return { average: gate.avgTurnTimeMinutes || 0, moves: gate.totalMoves || 0 };
  }

  getTrainMetrics(terminal: TerminalLevel) {
    const trains = this.iohTrainInfo?.terminals?.[terminal.milepost] || {};
    return { inbound: trains.inboundTrains?.length || 0, outbound: trains.outboundTrains?.length || 0 };
  }

  getTrainMeterValues(terminal: TerminalLevel) {
    const metrics = this.getTrainMetrics(terminal);
    return [
      { label: 'Inbound', value: metrics.inbound, color: '#2E9DF4' },
      { label: 'Outbound', value: metrics.outbound, color: '#f5b322' }
    ];
  }

  getMeterMax(values: { value: number }[]) {
    return values.reduce((total, item) => total + (item.value || 0), 0) || 1;
  }

  getEquipmentMeterData(terminal: TerminalLevel) {
    const values = this.getEquipmentMeterValues(terminal);
    return { values, max: this.getMeterMax(values) };
  }

  getTrainMeterData(terminal: TerminalLevel) {
    const values = this.getTrainMeterValues(terminal);
    return { values, max: this.getMeterMax(values) };
  }

  gateStatus(minutes: number) {
    return minutes > this.env.appConfig.iohGateAvgTurnTimeMints ? 'Poor' : 'Fast';
  }

  formatGateTime(minutes: number) {
     const roundedMinutes = Math.round(minutes)
     if (roundedMinutes < 60) return `${roundedMinutes} min`;
     const hours = Math.floor(roundedMinutes / 60);
     const remainingMinutes = roundedMinutes % 60;
    return remainingMinutes ? `${hours} hr ${remainingMinutes} min` : `${hours} hr`;
  }
}
